/**
 * Delivery UI — Zero-Knowledge / peer legacy (Android).
 *
 * Il pushBlob avviene UNA sola volta (con retry rete silenzioso).
 * Nessun timer ACK, nessun alert. pending → false al successo trasporto.
 */
import { patchMessage } from '../storage/chatStore.ts'

/** @deprecated Compat — ACK non più atteso. */
export const DELIVER_ACK_TIMEOUT_MS = 0
/** @deprecated Compat — retry disabilitati. */
export const DELIVER_ACK_MAX_RETRIES = 0

const waiters = new Map<string, number>()

type OutboxRetry = {
  messageId: string
  contactId: string
  attempt: number
  timer: number
  retryFn: () => Promise<boolean>
}

const outbox = new Map<string, OutboxRetry>()
const OUTBOX_MAX_ATTEMPTS = 8
const OUTBOX_BASE_MS = 2_000

/** Cancella timer residui (build vecchie / hot reload). */
export function clearDeliverAckWaiter(messageId: string): void {
  const id = String(messageId || '').trim()
  if (!id) return
  const timer = waiters.get(id)
  if (timer != null) {
    window.clearTimeout(timer)
    waiters.delete(id)
  }
}

/** Ferma TUTTI i waiter ACK in memoria. */
export function clearAllDeliverAckWaiters(): void {
  for (const [, timer] of waiters) {
    window.clearTimeout(timer)
  }
  waiters.clear()
}

export function clearOutboundRetry(messageId: string): void {
  const id = String(messageId || '').trim()
  const row = outbox.get(id)
  if (!row) return
  window.clearTimeout(row.timer)
  outbox.delete(id)
}

export function clearAllOutboundRetries(): void {
  for (const [, row] of outbox) {
    window.clearTimeout(row.timer)
  }
  outbox.clear()
}

/** Peer ha confermato (se arriva) → pending false. Opzionale. */
export async function applyIncomingDeliverAck(messageId: string): Promise<void> {
  const id = String(messageId || '').trim()
  if (!id) return
  clearDeliverAckWaiter(id)
  clearOutboundRetry(id)
  await patchMessage(id, { pending: false })
}

/**
 * Dopo push OK: marca SENT subito. Nessun setTimeout ACK.
 */
export function armDeliverAckWaiter(opts: {
  messageId: string
  contactId: string
  /** @deprecated Ignorato. */
  retrySend?: () => Promise<boolean>
}): void {
  const messageId = opts.messageId.trim()
  if (!messageId) return
  clearDeliverAckWaiter(messageId)
  clearOutboundRetry(messageId)
  void patchMessage(messageId, { pending: false })
}

/**
 * Push fallito: lascia pending in UI e ritenta in background (no alert, no throw).
 */
export function scheduleOutboundRetry(opts: {
  messageId: string
  contactId: string
  retryFn: () => Promise<boolean>
}): void {
  const messageId = opts.messageId.trim()
  if (!messageId) return
  clearOutboundRetry(messageId)

  const run = (attempt: number) => {
    const delay = attempt === 0 ? 800 : Math.min(30_000, OUTBOX_BASE_MS * Math.pow(1.6, attempt))
    const timer = window.setTimeout(() => {
      void (async () => {
        if (!outbox.has(messageId)) return
        let ok = false
        try {
          ok = await opts.retryFn()
        } catch (e) {
          console.warn('[ZION] outbound retry suppressed', e)
        }
        if (ok) {
          outbox.delete(messageId)
          await patchMessage(messageId, { pending: false })
          return
        }
        if (attempt + 1 >= OUTBOX_MAX_ATTEMPTS) {
          outbox.delete(messageId)
          return
        }
        run(attempt + 1)
      })()
    }, delay)
    outbox.set(messageId, {
      messageId,
      contactId: opts.contactId,
      attempt,
      timer,
      retryFn: opts.retryFn,
    })
  }

  run(0)
}

export function hasDeliverAckWaiter(messageId: string): boolean {
  return waiters.has(messageId.trim())
}
